import ButtonSave from "@View/components/buttons/ButtonSave";
import DefaultInput from "@View/components/formInputs/DefaultInput";
import ModalTittle from "@View/components/ModalTittle";
import ModalWrapper from "@View/components/ModalWrapper";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import getAPI from "@API/index";
import { FetchOnceAutocompleteMultiple } from "@Components/form/control/FetchOnceAutocompleteMultiple";
import { useTranslation } from "react-i18next";
import FormDateRow from "@View/components/formRows/FormDateRow";
import { parseDate, formatDateShort, normalizeDateToUTC } from "@Utils/dateActions";
import { useSelector } from "react-redux";
import {
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Divider,
} from "@material-ui/core";

const EditGroupConsolidationModal = ({
  customerId,
  group,
  consolidation,
  open,
  handleOpen,
  handleClose,
  reloadData,
}) => {
  const { t } = useTranslation();
  const customer = useSelector((state) => state.customer?.customer ?? {});

  const [newData, setNewData] = useState({
    name: "",
    startDate: new Date(),
    endDate: new Date(),
  });

  const [clientError, setClientError] = useState(false);
  const [clientVal, setClientVal] = useState([]);
  const [originalClientIds, setOriginalClientIds] = useState([]);
  const [originalDates, setOriginalDates] = useState({ startDate: null, endDate: null });
  const [clientDateRanges, setClientDateRanges] = useState({});
  const [isLoadingDateRanges, setIsLoadingDateRanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [recalculateOnSave, setRecalculateOnSave] = useState(true);

  const startDateMin = parseDate(group.consolidationPeriod?.startDate, "from") || new Date();
  const endDateMax = parseDate(group.consolidationPeriod?.endDate, "to") || new Date();

  const handleChange = (value, label) => {
    setNewData({ ...newData, [label]: value });
  };

  // Load date ranges for all customer clients
  const loadClientDateRanges = useCallback(async () => {
    if (!customer.clients || customer.clients.length === 0) return;

    setIsLoadingDateRanges(true);
    try {
      const clientIds = customer.clients.map(c => c.id);
      const response = await getAPI().request({
        method: "post",
        url: `/api/customer/${customerId}/consolidation/client-date-ranges`,
        data: clientIds,
      });

      const data = response.data || response;
      if (data && typeof data === 'object') {
        setClientDateRanges(data);
      }
    } catch (error) {
      console.error("Failed to load client date ranges:", error);
    } finally {
      setIsLoadingDateRanges(false);
    }
  }, [customerId, customer.clients]);

  // Initialize modal when consolidation changes
  useEffect(() => {
    if (open && consolidation) {
      const startDate = parseDate(consolidation.startDate, "from") || new Date();
      const endDate = parseDate(consolidation.endDate, "to") || new Date();

      setNewData({
        name: consolidation.name,
        startDate,
        endDate,
      });
      setOriginalDates({ startDate, endDate });

      const clients = consolidation.clients.map((i) => ({ id: i.id, name: i.name }));
      setClientVal(clients);
      setOriginalClientIds(clients.map(c => c.id));

      loadClientDateRanges();
    }
  }, [open, consolidation, loadClientDateRanges]);

  // Fetch options for autocomplete - all customer clients
  const searchClients = useCallback(async () => {
    return (customer.clients || []).map((client) => ({
      id: client.id,
      name: client.name,
      externalId: client.externalId,
    }));
  }, [customer.clients]);

  const handleClientChange = (newValue) => {
    setClientVal(newValue);
    if (clientError && newValue.length > 0) {
      setClientError(false);
    }
  };

  // Helper to find date range by client ID (handles GUID format differences)
  const findDateRange = useCallback((clientId, dateRanges) => {
    if (!clientId || !dateRanges) return null;
    const normalizedId = clientId.toLowerCase();
    return dateRanges[clientId] || dateRanges[normalizedId] ||
      Object.entries(dateRanges).find(([key]) => key.toLowerCase() === normalizedId)?.[1];
  }, []);

  // Render option with date range
  const renderClientOption = useCallback((option) => {
    const dateRange = findDateRange(option.id, clientDateRanges);
    const dateRangeText = dateRange && dateRange.minDate && dateRange.maxDate
      ? ` (${formatDateShort(dateRange.minDate)} - ${formatDateShort(dateRange.maxDate)})`
      : " (нет данных)";

    const isInGroup = group?.clients?.some(c => c.id === option.id);
    const isInConsolidation = originalClientIds.includes(option.id);

    return (
      <span>
        {option.name}
        <span style={{ color: "#888", fontSize: "0.85em" }}>
          {dateRangeText}
          {isInConsolidation && <span style={{ color: "#2196f3", marginLeft: "8px" }}>(в консолидации)</span>}
          {!isInConsolidation && isInGroup && <span style={{ color: "#4caf50", marginLeft: "8px" }}>(в группе)</span>}
        </span>
      </span>
    );
  }, [findDateRange, clientDateRanges, group?.clients, originalClientIds]);

  // Get option label with date range
  const getOptionLabelWithRange = useCallback((option) => {
    const dateRange = findDateRange(option.id, clientDateRanges);
    const dateRangeText = dateRange && dateRange.minDate && dateRange.maxDate
      ? ` (${formatDateShort(dateRange.minDate)} - ${formatDateShort(dateRange.maxDate)})`
      : "";
    return `${option.name}${dateRangeText}`;
  }, [findDateRange, clientDateRanges]);

  // Determine what clients were added/removed
  const clientChanges = useMemo(() => {
    const currentIds = clientVal.map(c => c.id);
    const addClientIds = currentIds.filter(id => !originalClientIds.includes(id));
    const removeClientIds = originalClientIds.filter(id => !currentIds.includes(id));
    const hasChanges = addClientIds.length > 0 || removeClientIds.length > 0;

    return { addClientIds, removeClientIds, hasChanges };
  }, [clientVal, originalClientIds]);

  // Determine if dates have changed
  const datesChanged = useMemo(() => {
    if (!originalDates.startDate || !originalDates.endDate) return false;
    return (
      newData.startDate?.getTime() !== originalDates.startDate?.getTime() ||
      newData.endDate?.getTime() !== originalDates.endDate?.getTime()
    );
  }, [newData.startDate, newData.endDate, originalDates]);

  // Check if name has changed
  const nameChanged = consolidation?.name !== newData.name;

  // Has any changes
  const hasAnyChanges = clientChanges.hasChanges || datesChanged || nameChanged;

  // Save using PATCH /clients endpoint
  const updateGroupConsolidation = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (clientVal.length === 0) {
      setClientError(true);
      return;
    }
    if (newData.name.trim().length === 0) {
      return;
    }

    setIsSaving(true);

    try {
      // Use PUT endpoint for full update
      await getAPI().request({
        method: "put",
        url: `/api/customer/${customerId}/consolidation/${consolidation.id}/update`,
        data: {
          name: newData.name,
          clientIds: clientVal.map(c => c.id),
          startDate: normalizeDateToUTC(newData.startDate),
          endDate: normalizeDateToUTC(newData.endDate),
        },
        successText: "snackbar.success",
      });

      handleClose();
      if (reloadData) {
        reloadData();
      }
    } catch (error) {
      console.error("Failed to update consolidation:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Recalculate consolidation
  const handleRecalculate = async () => {
    setIsRecalculating(true);

    try {
      await getAPI().request({
        method: "post",
        url: `/api/customer/${customerId}/consolidation/${consolidation.id}/recalculate`,
        data: {
          fromDate: null, // Full recalculation
          toDate: null,
        },
        successText: "snackbar.success",
      });

      handleClose();
      if (reloadData) {
        reloadData();
      }
    } catch (error) {
      console.error("Failed to recalculate consolidation:", error);
    } finally {
      setIsRecalculating(false);
    }
  };

  const isLoading = isSaving || isRecalculating || isLoadingDateRanges;

  return (
    <ModalWrapper open={open} handleOpen={handleOpen} handleClose={handleClose}>
      <form onSubmit={updateGroupConsolidation}>
        <ModalTittle>h1.Edit Request</ModalTittle>

        <DefaultInput
          value={newData.name}
          label="inputs.GroupName"
          onChange={(value) => handleChange(value, "name")}
        />

        <FetchOnceAutocompleteMultiple
          key={Object.keys(clientDateRanges).length}
          value={clientVal}
          id="ClientCapacity"
          label={t("ClientCapacityTitle")}
          fetchOptions={searchClients}
          onChange={handleClientChange}
          error={clientError}
          helperText={clientError ? t("FieldRequired") : ""}
          renderOptionCustom={renderClientOption}
          getOptionLabelCustom={getOptionLabelWithRange}
        />

        <FormDateRow
          value={newData.startDate}
          label="inputs.Start Date"
          onChange={(_, value) => {
            // Set to first day of the selected month
            const date = new Date(value);
            date.setDate(1);
            handleChange(date, "startDate");
          }}
          dateFormat="dd.MM.yyyy"
          minDate={startDateMin}
          maxDate={endDateMax}
        />

        <FormDateRow
          value={newData.endDate}
          label="inputs.End Date"
          onChange={(_, value) => {
            // Set to last day of the selected month
            const date = new Date(value);
            date.setMonth(date.getMonth() + 1);
            date.setDate(0);
            handleChange(date, "endDate");
          }}
          dateFormat="dd.MM.yyyy"
          minDate={startDateMin}
          maxDate={endDateMax}
        />

        <ButtonSave disabled={isLoading || !hasAnyChanges}>
          {isSaving ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "buttons.Save"
          )}
        </ButtonSave>

      </form>
    </ModalWrapper>
  );
};

export default EditGroupConsolidationModal;
